import type { CancelServiceOrderDto } from "@/dtos/CancelServiceOrderDto";
import type { CreateServiceOrderDto } from "@/dtos/CreateOrderServiceDto";
import type { ReturnServiceOrderDto } from "@/dtos/ReturnServiceOrderDto";
import type { SuspendServiceOrderDto } from "@/dtos/SuspendServiceOrderDto";
import type { UpdateServiceOrderDto } from "@/dtos/UpdateServiceOrderDto";
import api from "@/services/api";
import { useMutation, useQuery } from "@tanstack/react-query";

interface GetServiceOrdersParams {
  search?: string;
  sectorId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export const useCreateServiceOrder = () => {
  return useMutation({
    mutationKey: ["createServiceOrder"],
    mutationFn: async (createServiceOrderDto: CreateServiceOrderDto) => {
      const response = await api.post("/work-orders", createServiceOrderDto);

      return response.data;
    },
  });
};

export const useGetServiceOrders = (params?: GetServiceOrdersParams) => {
  return useQuery({
    queryKey: ["getServiceOrders", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params?.search) {
        searchParams.append("search", params.search);
      }

      if (params?.sectorId && params.sectorId !== "all") {
        searchParams.append("sectorId", params.sectorId);
      }

      if (params?.status && params.status !== "all") {
        searchParams.append("status", params.status);
      }

      if (params?.page) {
        searchParams.append("page", params.page.toString());
      }

      if (params?.limit) {
        searchParams.append("limit", params.limit.toString());
      }

      const queryString = searchParams.toString();
      const url = queryString ? `/work-orders?${queryString}` : "/work-orders";

      const response = await api.get(url);
      return response.data;
    },
  });
};

export const useGetServiceOrderById = (id: string) => {
  return useQuery({
    queryKey: ["getServiceOrderById", id],
    queryFn: async () => {
      const response = await api.get(`/work-orders/${id}`);
      return response.data;
    },
  });
};

export const useUpdateServiceOrder = () => {
  return useMutation({
    mutationKey: ["updateServiceOrder"],
    mutationFn: async (
      updateServiceOrderDto: UpdateServiceOrderDto & {
        originalOccurrenceIds?: string[];
        occurrenceIds?: string[];
      },
    ) => {
      const originalIds = updateServiceOrderDto.originalOccurrenceIds || [];
      const newIds = updateServiceOrderDto.occurrenceIds || [];

      const addOccurrenceIds = newIds.filter((id) => !originalIds.includes(id));
      const removeOccurrenceIds = originalIds.filter(
        (id) => !newIds.includes(id),
      );

      const requestBody: any = {
        title: updateServiceOrderDto.title,
        description: updateServiceOrderDto.description,
        priority: updateServiceOrderDto.priority,
        sectorId: updateServiceOrderDto.sectorId,
        assignedToId: updateServiceOrderDto.assignedToId,
        assignedTeamId: updateServiceOrderDto.assignedTeamId,
        estimatedStartDate: updateServiceOrderDto.estimatedStartDate,
        estimatedEndDate: updateServiceOrderDto.estimatedEndDate,
      };

      if (updateServiceOrderDto.status) {
        requestBody.status = updateServiceOrderDto.status;
      }

      if (updateServiceOrderDto.priorityJustification) {
        requestBody.priorityJustification =
          updateServiceOrderDto.priorityJustification;
      }

      if (updateServiceOrderDto.statusJustification) {
        requestBody.statusJustification =
          updateServiceOrderDto.statusJustification;
      }

      if (addOccurrenceIds.length > 0) {
        requestBody.addOccurrenceIds = addOccurrenceIds;
      }

      if (removeOccurrenceIds.length > 0) {
        requestBody.removeOccurrenceIds = removeOccurrenceIds;
      }

      const response = await api.patch(
        `/work-orders/${updateServiceOrderDto.id}`,
        requestBody,
      );
      return response.data;
    },
  });
};

export const useGetServiceOrderHistory = (id: string) => {
  return useQuery({
    queryKey: ["getServiceOrderHistory", id],
    queryFn: async () => {
      const response = await api.get(`/work-orders/${id}/history`);

      return response.data;
    },
  });
};

export const useStartServiceOrder = () => {
  return useMutation({
    mutationKey: ["startServiceOrder"],
    mutationFn: async (id: string) => {
      const response = await api.post(`/work-orders/${id}/start`);

      return response.data;
    },
  });
};

export const useSuspendServiceOrder = () => {
  return useMutation({
    mutationKey: ["suspendServiceOrder"],
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(
        `/work-orders/${id}/suspend`,
        { reason },
      );

      return response.data;
    },
  });
};

export const useResumeServiceOrder = () => {
  return useMutation({
    mutationKey: ["resumeServiceOrder"],
    mutationFn: async (id: string) => {
      const response = await api.post(`/work-orders/${id}/resume`);
      return response.data;
    },
  });
};

export const useCompleteServiceOrder = () => {
  return useMutation({
    mutationKey: ["completeServiceOrder"],
    mutationFn: async (id: string) => {
      const response = await api.post(`/work-orders/${id}/complete`);
      return response.data;
    },
  });
};

export const useReturnServiceOrder = () => {
  return useMutation({
    mutationKey: ["returnServiceOrder"],
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(
        `/work-orders/${id}/return-to-ouvidoria`,
        { reason },
      );
      return response.data;
    },
  });
};

export const useCancelServiceOrder = () => {
  return useMutation({
    mutationKey: ["cancelServiceOrder"],
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(
        `/work-orders/${id}/cancel`,
        { reason },
      );
      return response.data;
    },
  });
};

// Novo fluxo de validação

export const useEvaluateServiceOrder = () => {
  return useMutation({
    mutationKey: ["evaluateServiceOrder"],
    mutationFn: async ({
      id,
      completionNotes,
      attachments,
    }: {
      id: string;
      completionNotes?: string;
      attachments?: string[];
    }) => {
      const response = await api.post(`/work-orders/${id}/evaluate`, {
        completionNotes,
        attachments,
      });
      return response.data;
    },
  });
};

export const useValidateServiceOrder = () => {
  return useMutation({
    mutationKey: ["validateServiceOrder"],
    mutationFn: async (id: string) => {
      const response = await api.post(`/work-orders/${id}/complete`);
      return response.data;
    },
  });
};

export const useRejectServiceOrderEvaluation = () => {
  return useMutation({
    mutationKey: ["rejectServiceOrderEvaluation"],
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await api.post(`/work-orders/${id}/reject-evaluation`, {
        reason,
      });
      return response.data;
    },
  });
};

export const useGetAwaitingValidationServiceOrders = (params?: {
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ["getAwaitingValidationServiceOrders", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.append("status", "AWAITING_VALIDATION");

      if (params?.page) {
        searchParams.append("page", params.page.toString());
      }

      if (params?.limit) {
        searchParams.append("limit", params.limit.toString());
      }

      const response = await api.get(`/work-orders?${searchParams.toString()}`);
      return response.data;
    },
    refetchInterval: 30000, // Atualiza a cada 30 segundos
  });
};
